import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useState,
  type ReactNode,
} from "react";
import { authCheck, adminLogin, getMe, type CurrentUser } from "../api/client";

interface AdminAuthContextType {
  isAuthenticated: boolean;
  isLoading: boolean;
  user: CurrentUser | null;
  login: (email: string, password: string) => Promise<boolean>;
  logout: () => void;
}

const AdminAuthContext = createContext<AdminAuthContextType | null>(null);

export function AdminAuthProvider({ children }: { children: ReactNode }) {
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [user, setUser] = useState<CurrentUser | null>(null);

  useEffect(() => {
    const token = localStorage.getItem("admin-token");
    if (token) {
      authCheck()
        .then(() => {
          setIsAuthenticated(true);
          getMe().then(setUser).catch(() => {});
        })
        .catch(() => {
          localStorage.removeItem("admin-token");
          setIsAuthenticated(false);
        })
        .finally(() => setIsLoading(false));
    } else {
      setIsLoading(false);
    }
  }, []);

  const login = useCallback(async (email: string, password: string): Promise<boolean> => {
    try {
      const token = await adminLogin(email, password);
      localStorage.setItem("admin-token", token);
      await authCheck();
      setIsAuthenticated(true);
      getMe().then(setUser).catch(() => {});
      return true;
    } catch {
      localStorage.removeItem("admin-token");
      setIsAuthenticated(false);
      return false;
    }
  }, []);

  const logout = useCallback(() => {
    localStorage.removeItem("admin-token");
    setIsAuthenticated(false);
    setUser(null);
  }, []);

  return (
    <AdminAuthContext.Provider
      value={{ isAuthenticated, isLoading, user, login, logout }}
    >
      {children}
    </AdminAuthContext.Provider>
  );
}

export function useAdminAuth() {
  const ctx = useContext(AdminAuthContext);
  if (!ctx) throw new Error("useAdminAuth must be used within AdminAuthProvider");
  return ctx;
}
